import { generatePath } from 'react-router-dom';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { User, UserTypes } from 'types';
import { calculateOffset } from 'utils/helpers';
import { filterEmptyFields } from 'utils/object';
import { updateAuthUser } from 'store/slices/auth';
import { addParamsToUrl, addSearchParams } from 'utils/query';
import {
  cacheByIdArg,
  providesList,
  invalidatesList,
  cacheByIdArgProperty,
} from 'utils/query-cache';

import { apiQuery } from '../../query';
import { companyApi, tagTypes as companyTagTypes } from '../company';

import {
  UserData,
  RoleData,
  UsersRequest,
  UsersResponse,
  UserFormValues,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  UserChangeEmailRequest,
  UserConfirmChangeEmail,
  UserChangeEmailResponse,
  UserUpdatePasswordRequest,
  ValidateResetTokenResponse,
  CreateApprovalPasswordRequest,
  UpdateApprovalPasswordRequest,
  VerifyApprovalPasswordResponse,
} from './types';

const tagType = 'Users';

const handleFormData = (data: Partial<UserFormValues>) => {
  const resultData = { ...data };

  if (resultData.role && UserTypes.ops.includes(resultData.role)) {
    delete resultData.companyId;
  }

  return resultData;
};

export const usersApi = createApi({
  baseQuery: apiQuery,
  tagTypes: [tagType],
  reducerPath: 'usersApi',
  endpoints: (build) => ({
    listRoles: build.query<RoleData[], void>({
      providesTags: [tagType],
      query: () => ({
        method: 'get',
        url: URL.USER_ROLES,
      }),
    }),

    get: build.query<UserData, string>({
      providesTags: cacheByIdArg(tagType),
      query: (id) => ({
        method: 'get',
        url: URL.USER_DETAILS.replace(':id', id),
      }),
    }),

    remove: build.mutation<void, string>({
      invalidatesTags: invalidatesList(tagType),
      query: (id) => ({
        method: 'delete',
        url: URL.USER_DETAILS.replace(':id', id),
      }),
    }),

    resetPassword: build.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      invalidatesTags: [tagType],
      query: (data) => ({
        data,
        method: 'post',
        url: URL.USER_RESET_PASSWORD,
      }),
    }),

    validateResetToken: build.query<ValidateResetTokenResponse, string>({
      providesTags: [tagType],
      query: (token) => ({
        method: 'get',
        url: addParamsToUrl(URL.USER_VALIDATE_RESET_TOKEN, { token }),
      }),
    }),

    forgotPassword: build.mutation<
      ForgotPasswordResponse,
      ForgotPasswordRequest
    >({
      invalidatesTags: [tagType],
      query: (data) => ({
        data,
        method: 'post',
        url: URL.USER_SEND_RESET_PASSWORD,
      }),
    }),

    create: build.mutation<void, UserFormValues>({
      invalidatesTags: invalidatesList(tagType),
      query: (data) => {
        return {
          method: 'post',
          url: URL.USERS,
          data: filterEmptyFields(handleFormData(data)),
        };
      },
    }),

    updatePassword: build.mutation<User, UserUpdatePasswordRequest>({
      invalidatesTags: invalidatesList(tagType),
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.USER_UPDATE_PASSWORD, { id: String(id) }),
      }),
    }),

    changeEmail: build.mutation<
      UserChangeEmailResponse,
      UserChangeEmailRequest
    >({
      invalidatesTags: [tagType],
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.USER_CHANGE_EMAIL, { id: String(id) }),
      }),
    }),

    updateApprovalPassword: build.mutation<User, UpdateApprovalPasswordRequest>(
      {
        invalidatesTags: invalidatesList(tagType),
        query: ({ id, ...data }) => ({
          data,
          method: 'patch',
          url: URL.USER_APPROVAL_PASSWORD.replace(':id', String(id)),
        }),
      }
    ),

    createApprovalPassword: build.mutation<User, CreateApprovalPasswordRequest>(
      {
        invalidatesTags: invalidatesList(tagType),
        query: ({ id, approvalPassword }) => ({
          method: 'post',
          data: { approvalPassword },
          url: URL.USER_APPROVAL_PASSWORD.replace(':id', String(id)),
        }),
      }
    ),

    edit: build.mutation<void, UserFormValues>({
      invalidatesTags: cacheByIdArgProperty(tagType),
      query: ({ id, ...data }) => {
        return {
          method: 'patch',
          url: URL.USER_DETAILS.replace(':id', id),
          data: filterEmptyFields({
            ...handleFormData(data),
            id,
          }),
        };
      },
    }),

    verifyApprovalPassword: build.mutation<
      VerifyApprovalPasswordResponse,
      CreateApprovalPasswordRequest
    >({
      invalidatesTags: invalidatesList(tagType),
      query: ({ id, approvalPassword }) => ({
        method: 'post',
        data: { approvalPassword },
        url: URL.USER_VERIFY_APPROVAL_PASSWORD.replace(':id', String(id)),
      }),
    }),

    list: build.query<UsersResponse, UsersRequest | void>({
      providesTags: (result) => providesList(tagType)(result?.rows),
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };

        return {
          method: 'get',
          url: addSearchParams(URL.USERS, searchParams),
        };
      },
    }),

    confirmChangeEmail: build.mutation<
      UserChangeEmailResponse,
      UserConfirmChangeEmail
    >({
      invalidatesTags: [tagType],
      query: ({ id, ...data }) => ({
        data,
        method: 'post',
        url: generatePath(URL.USER_VERIFY_CHANGE_EMAIL_CODE, {
          id: String(id),
        }),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(companyApi.util.invalidateTags(companyTagTypes));
          dispatch(updateAuthUser({ email: args.newEmail }));
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useGetQuery,
  useListQuery,
  useEditMutation,
  useListRolesQuery,
  useRemoveMutation,
  useCreateMutation,
  useChangeEmailMutation,
  useResetPasswordMutation,
  useUpdatePasswordMutation,
  useForgotPasswordMutation,
  useValidateResetTokenQuery,
  useConfirmChangeEmailMutation,
  useVerifyApprovalPasswordMutation,
  useCreateApprovalPasswordMutation,
  useUpdateApprovalPasswordMutation,
} = usersApi;

export type { UserData, RoleData, UserFormValues };
